import { useState, useEffect } from "react";
import axios from "axios";
import { Star, Gift, Sparkles, X, Send, Check } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  ar: {
    title: "احصل على ٣ اقتراحات أسعار مجانًا ",
    subtitle:
      "اقتراحات أسعار مبنية على جدول أسعار مرجعي للسوق السعودي ومدعومة بالذكاء الاصطناعي. شارك تقييمك واقتراحك + أدخل بريدك واسمك، وستُضاف ٣ اقتراحات أسعار مجانًا فورًا لحسابك.",
    starLabel: "تقييمك *",
    rateHint: "اضغط النجوم للتقييم",
    nameLabel: "الاسم *",
    namePlaceholder: "اكتب اسمك الكامل",
    emailLabel: "البريد الإلكتروني *",
    emailPlaceholder: "example@email.com",
    commentLabel: "اقتراحك أو تعليقك * (١٠ أحرف على الأقل)",
    commentPlaceholder:
      "ما الذي أعجبك؟ وما الذي نضيفه لتحسين الحاسبة أو منصة برولانسر؟",
    submit: "استلم ٣ اقتراحات أسعار مجانًا",
    submitting: "جارٍ الإرسال…",
    skip: "لاحقًا",
    privacy:
      "لن نشارك بريدك مع أي طرف ثالث. يستخدم فقط لربط رصيدك المجاني بحسابك.",
    labels: ["سيّئ", "مقبول", "جيد", "جيد جدًا", "ممتاز"],
    errRating: "اختر عدد النجوم أولًا",
    errEmail: "أدخل بريد إلكتروني صالح",
    errName: "الاسم مطلوب (حرفين على الأقل)",
    errComment: "اكتب تعليقًا من ١٠ أحرف فأكثر",
    successTitle: "تم إضافة ٣ اقتراحات أسعار مجانية ",
    successSub: "رصيدك الجديد:",
    successBtn: "ابدأ استخدام اقتراحات الأسعار",
    alreadyClaimed: "استلمت الهدية سابقًا بهذا البريد — جرّب بريدًا جديدًا للمحاولة مرة أخرى.",
    bonusBadge: "+٣ اقتراحات أسعار",
    dir: "rtl",
    font: "font-arabic",
  },
  en: {
    title: "Get 3 Free Price Suggestions ",
    subtitle:
      "Price suggestions built on reference Saudi market rates and powered by AI. Share your rating + comment and add your email + name — we'll instantly add 3 free price suggestions to your account.",
    starLabel: "Your rating *",
    rateHint: "Tap a star to rate",
    nameLabel: "Name *",
    namePlaceholder: "Your full name",
    emailLabel: "Email *",
    emailPlaceholder: "example@email.com",
    commentLabel: "Your feedback * (min 10 characters)",
    commentPlaceholder: "What did you like? What should we improve?",
    submit: "Claim 3 free price suggestions",
    submitting: "Sending…",
    skip: "Later",
    privacy:
      "We'll never share your email with anyone. It's used only to link your free credits to your account.",
    labels: ["Poor", "Fair", "Good", "Very good", "Excellent"],
    errRating: "Please pick a rating first",
    errEmail: "Enter a valid email address",
    errName: "Name is required (min 2 characters)",
    errComment: "Please write at least 10 characters",
    successTitle: "+3 free price suggestions added ",
    successSub: "Your new balance:",
    successBtn: "Start using price suggestions",
    alreadyClaimed:
      "This email already claimed the bonus — try a new one if you want another.",
    bonusBadge: "+3 price suggestions",
    dir: "ltr",
    font: "font-outfit",
  },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function BonusFeedbackModal({
  open,
  onOpenChange,
  lang = "ar",
  theme = "dark",
  serviceType = "",
  onCreditsGranted,
}) {
  const dark = theme === "dark";
  const L = lang === "ar" ? T.ar : T.en;

  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [name, setName] = useState(() => localStorage.getItem("pl_name") || "");
  const [email, setEmail] = useState(() => localStorage.getItem("pl_email") || "");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [newBalance, setNewBalance] = useState(0);

  // Reset state whenever the dialog opens fresh.
  useEffect(() => {
    if (open) {
      setRating(5);
      setHover(0);
      setComment("");
      setDone(false);
      setNewBalance(0);
      setName(localStorage.getItem("pl_name") || "");
      setEmail(localStorage.getItem("pl_email") || "");
    }
  }, [open]);

  const submit = async () => {
    if (!rating) return toast.error(L.errRating);
    if (name.trim().length < 2) return toast.error(L.errName);
    if (!EMAIL_RE.test(email.trim())) return toast.error(L.errEmail);
    if (comment.trim().length < 10) return toast.error(L.errComment);

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/feedback/claim-bonus`, {
        rating,
        comment: comment.trim(),
        name: name.trim(),
        email: email.trim().toLowerCase(),
        service_type: serviceType || "",
        language: lang,
      });
      localStorage.setItem("pl_name", name.trim());
      localStorage.setItem("pl_email", email.trim().toLowerCase());
      localStorage.setItem("pl_bonus_claimed", "1");
      setNewBalance(res.data.credits_remaining || 0);
      setDone(true);
      toast.success(L.successTitle);
      if (typeof onCreditsGranted === "function") {
        onCreditsGranted({
          email: email.trim().toLowerCase(),
          credits_remaining: res.data.credits_remaining || 0,
          credits_added: res.data.credits_added || 3,
        });
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        localStorage.setItem("pl_bonus_claimed", "1");
        toast.error(L.alreadyClaimed);
      } else {
        const detail = err?.response?.data?.detail;
        toast.error(
          typeof detail === "string"
            ? detail
            : lang === "ar"
            ? "تعذّر الإرسال — حاول لاحقًا"
            : "Failed to send — try later"
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="bonus-feedback-modal"
        dir={L.dir}
        className={`${L.font} w-[94vw] max-w-sm sm:max-w-md p-0 overflow-hidden border-0 max-h-[85vh] overflow-y-auto rounded-2xl ${
          dark ? "bg-[#182138] text-white" : "bg-white text-[#182138]"
        }`}
      >
        {/* Top gradient banner */}
        <div className="relative px-4 pt-4 pb-3 bg-gradient-to-br from-[#18c98f]/30 via-[#18c98f]/15 to-transparent">
          <div className="absolute top-2 left-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-[#18c98f] text-white text-[10px] font-extrabold px-2 py-0.5">
              <Gift className="h-2.5 w-2.5" />
              {L.bonusBadge}
            </span>
          </div>
          <DialogHeader className={`${lang === "ar" ? "text-right" : "text-left"} space-y-1 mt-4`}>
            <DialogTitle
              data-testid="bonus-modal-title"
              className={`text-base sm:text-lg font-extrabold leading-tight ${
                dark ? "text-white" : "text-[#182138]"
              }`}
            >
              {L.title}
            </DialogTitle>
            <DialogDescription
              className={`text-xs leading-snug ${dark ? "text-white/70" : "text-[#2d2c35]/80"}`}
            >
              {L.subtitle}
            </DialogDescription>
          </DialogHeader>
        </div>

        {done ? (
          <div className="px-6 pb-6 pt-2" data-testid="bonus-success-view">
            <div
              className={`rounded-2xl p-5 flex items-center gap-3 ${
                dark ? "bg-[#18c98f]/15 border border-[#18c98f]/40" : "bg-[#18c98f]/10 border border-[#18c98f]/40"
              }`}
            >
              <div className="h-11 w-11 shrink-0 rounded-full bg-[#18c98f] text-white inline-flex items-center justify-center">
                <Check className="h-5 w-5" strokeWidth={3} />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`font-extrabold text-sm sm:text-base ${dark ? "text-white" : "text-[#182138]"}`}>
                  {L.successTitle}
                </div>
                <div className={`mt-0.5 text-xs ${dark ? "text-white/70" : "text-[#2d2c35]/70"}`}>
                  {L.successSub}{" "}
                  <span className="font-extrabold text-[#18c98f]" data-testid="bonus-new-balance">
                    {newBalance}
                  </span>{" "}
                  {lang === "ar" ? "اقتراح سعر" : "price credits"}
                </div>
              </div>
            </div>
            <button
              data-testid="bonus-success-cta"
              onClick={() => onOpenChange(false)}
              className="mt-5 w-full h-11 rounded-full bg-[#18c98f] text-white font-extrabold hover:brightness-110 shadow-[0_6px_16px_-6px_rgba(24,201,143,0.6)] inline-flex items-center justify-center gap-2"
            >
              <Sparkles className="h-4 w-4" />
              {L.successBtn}
            </button>
          </div>
        ) : (
          <div className="px-4 pb-4 pt-2 space-y-3">
            {/* Stars */}
            <div>
              <label className={`block text-xs font-bold mb-1.5 ${dark ? "text-white/80" : "text-[#2d2c35]"}`}>
                {L.starLabel}
              </label>
              <div className="flex items-center justify-center gap-1" onMouseLeave={() => setHover(0)}>
                {[1, 2, 3, 4, 5].map((n) => {
                  const active = (hover || rating) >= n;
                  return (
                    <button
                      key={n}
                      type="button"
                      data-testid={`bonus-star-${n}`}
                      onClick={() => setRating(n)}
                      onMouseEnter={() => setHover(n)}
                      className="h-9 w-9 inline-flex items-center justify-center rounded-xl transition-transform hover:scale-110 active:scale-95"
                      aria-label={`${n} star${n > 1 ? "s" : ""}`}
                    >
                      <Star
                        className={`h-6 w-6 transition-colors ${
                          active ? "fill-[#18c98f] text-[#18c98f]" : dark ? "text-white/25" : "text-[#2d2c35]/25"
                        }`}
                        strokeWidth={1.5}
                      />
                    </button>
                  );
                })}
              </div>
              <div className={`text-center text-[11px] mt-0.5 ${dark ? "text-white/60" : "text-[#2d2c35]/60"}`}>
                {rating > 0 ? L.labels[rating - 1] : L.rateHint}
              </div>
            </div>

            {/* Name */}
            <div>
              <input
                data-testid="bonus-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={L.nameLabel}
                maxLength={100}
                className={`w-full px-3 py-2 rounded-lg text-sm ${
                  dark
                    ? "bg-[#1f2a47] border border-[#2e3a5c] text-white placeholder:text-white/40 focus:border-[#18c98f]"
                    : "bg-white border border-[#e5e5ea] text-[#2d2c35] placeholder:text-[#2d2c35]/40 focus:border-[#18c98f]"
                } focus:outline-none`}
              />
            </div>

            {/* Email */}
            <div>
              <input
                data-testid="bonus-email-input"
                type="email"
                dir="ltr"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={L.emailLabel + " · " + L.emailPlaceholder}
                maxLength={200}
                className={`w-full px-3 py-2 rounded-lg text-sm ${
                  dark
                    ? "bg-[#1f2a47] border border-[#2e3a5c] text-white placeholder:text-white/40 focus:border-[#18c98f]"
                    : "bg-white border border-[#e5e5ea] text-[#2d2c35] placeholder:text-[#2d2c35]/40 focus:border-[#18c98f]"
                } focus:outline-none`}
              />
            </div>

            {/* Comment */}
            <div>
              <textarea
                data-testid="bonus-comment-input"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={L.commentLabel}
                maxLength={1000}
                rows={2}
                className={`w-full px-3 py-2 rounded-lg text-sm resize-none ${
                  dark
                    ? "bg-[#1f2a47] border border-[#2e3a5c] text-white placeholder:text-white/40 focus:border-[#18c98f]"
                    : "bg-white border border-[#e5e5ea] text-[#2d2c35] placeholder:text-[#2d2c35]/40 focus:border-[#18c98f]"
                } focus:outline-none`}
              />
              <div className={`mt-0.5 text-[10px] ${dark ? "text-white/50" : "text-[#2d2c35]/50"}`}>
                {comment.trim().length}/10
              </div>
            </div>

            {/* Submit */}
            <button
              data-testid="bonus-submit-btn"
              onClick={submit}
              disabled={submitting}
              className={`w-full h-10 rounded-full font-extrabold text-sm inline-flex items-center justify-center gap-2 transition-all bg-[#18c98f] text-white hover:brightness-110 disabled:opacity-60 shadow-[0_8px_20px_-8px_rgba(24,201,143,0.7)]`}
            >
              {submitting ? (
                <>
                  <Send className="h-4 w-4 animate-pulse" />
                  {L.submitting}
                </>
              ) : (
                <>
                  <Gift className="h-4 w-4" />
                  {L.submit}
                </>
              )}
            </button>

            <p className={`text-[9.5px] leading-tight text-center ${dark ? "text-white/50" : "text-[#2d2c35]/55"}`}>
              {L.privacy}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
